import type { RefObject } from "react";

import { EndTurnButton } from "#/components/game/EndTurnButton";
import { GameSettingsMenu } from "#/components/game/GameSettingsMenu";
import { UndoButton } from "#/components/game/UndoButton";
import { useEndTurnConfirmation } from "#/components/game/useEndTurnConfirmation";
import { PlayBottomDockBar } from "#/components/play/PlayMapPageShell/docks";
import { useInspectedUnit } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";
import { cn } from "#/lib/utils";

import type { TutorialController } from "./useTutorialController";

function TutorialDockActions({
  controller,
  onRestart,
}: {
  controller: TutorialController;
  onRestart: () => void;
}) {
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const { requestEndTurn, confirmDialog } = useEndTurnConfirmation({
    onConfirm: controller.endTurn,
    pending: isSubmitting,
    confirmBeforeEndTurn: true,
  });
  const highlightEndTurn = controller.prompt.highlight?.kind === "end-turn";
  const highlightUndo = controller.prompt.highlight?.kind === "undo";
  const blocked = controller.blocksInteraction;

  return (
    <>
      <div className={cn(highlightUndo && "rounded-md ring-4 ring-amber-400 animate-pulse")}>
        <UndoButton onClick={controller.undoLastMove} disabled={blocked || !controller.canUndo} />
      </div>
      <div className={cn(highlightEndTurn && "rounded-md ring-4 ring-amber-400 animate-pulse")}>
        <EndTurnButton
          onClick={requestEndTurn}
          disabled={blocked || !controller.canEndTurn}
          pending={isSubmitting}
        />
      </div>
      <GameSettingsMenu onRestart={onRestart} isRestarting={false} />
      {confirmDialog}
    </>
  );
}

export function TutorialBottomDock({
  bottomDockRef,
  controller,
  onRestart,
}: {
  bottomDockRef: RefObject<HTMLDivElement | null>;
  controller: TutorialController;
  onRestart: () => void;
}) {
  const localState = useGameSelector((s) => s.localState);
  const inspectedTile = useGameSelector((s) => s.hoveredTile);
  const inspectedUnit = useInspectedUnit();
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const dockActions =
    localState.status === "in_progress" ? (
      <TutorialDockActions controller={controller} onRestart={onRestart} />
    ) : undefined;

  return (
    <div
      ref={bottomDockRef}
      className="sticky bottom-[max(0px,env(safe-area-inset-bottom))] z-30 shrink-0"
    >
      <div className="bg-card shadow-[0_-4px_24px_rgba(0,0,0,0.08)]">
        <PlayBottomDockBar
          status={localState.status}
          localState={localState}
          inspectedTile={inspectedTile}
          inspectedUnit={inspectedUnit}
          handleEndTurn={controller.endTurn}
          canEndTurn={controller.canEndTurn}
          handleUndo={controller.undoLastMove}
          canUndo={controller.canUndo}
          isSubmitting={isSubmitting}
          handleRestart={onRestart}
          isRestarting={false}
          actions={dockActions}
        />
      </div>
    </div>
  );
}
